import React, { useState } from 'react';
import { Truck, Store, Globe, ChevronDown } from 'lucide-react';

interface DistributionItem {
  id: string;
  channel: string;
  percentage: number;
  amount: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  barColor: string;
}

const distributionData: DistributionItem[] = [
  {
    id: 'gt',
    channel: 'General Trade (GT)',
    percentage: 68,
    amount: '₹8,45,678',
    icon: Truck,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    barColor: 'bg-emerald-500',
  },
  {
    id: 'mt',
    channel: 'Modern Trade (MT)',
    percentage: 32,
    amount: '₹4,00,000',
    icon: Store,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    barColor: 'bg-orange-500',
  },
  {
    id: 'ecom',
    channel: 'E-Commerce',
    percentage: 8,
    amount: '₹98,765',
    icon: Globe,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    barColor: 'bg-purple-500',
  },
];

export const SalesDistribution: React.FC = () => {
  const [period, setPeriod] = useState<'This Month' | 'Last Month'>('This Month');

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#ECECF2] shadow-sm flex flex-col justify-between h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-bold text-slate-800">Sales Distribution</h3>

        {/* Dropdown Filter */}
        <div className="relative group">
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            {period}
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-100 rounded-lg shadow-lg hidden group-hover:block z-10">
            {(['This Month', 'Last Month'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-6">
        {distributionData.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.id} className="flex items-center gap-4">
              {/* Channel Icon */}
              <div className={`w-10 h-10 rounded-xl ${item.iconBg} ${item.iconColor} flex items-center justify-center shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>

              {/* Label, Progress Bar & Percentage */}
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                  <span>{item.channel}</span>
                  <span className="text-slate-400 font-semibold">{item.percentage}%</span>
                </div>
                {/* Progress bar container */}
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.barColor} rounded-full transition-all duration-500`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="text-right shrink-0 w-24">
                <span className="text-sm font-bold text-slate-800">{item.amount}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
